import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useState, useEffect, createContext, useContext, useCallback } from "react";
import { Toaster } from "@/components/ui/sonner";
import axios from "axios";

// Pages
import LandingPage from "@/pages/LandingPage";
import Dashboard from "@/pages/Dashboard";
import Patients from "@/pages/Patients";
import PARequests from "@/pages/PARequests";
import NewPARequest from "@/pages/NewPARequest";
import FormLibrary from "@/pages/FormLibrary";
import Analytics from "@/pages/Analytics";
import Settings from "@/pages/Settings";
import AuthCallback from "@/pages/AuthCallback";
import OrgSetup from "@/pages/OrgSetup";
import UserManagement from "@/pages/UserManagement";
import ReviewQueue from "@/pages/ReviewQueue";
import Appeals from "@/pages/Appeals";
import DenialWorkbench from "@/pages/DenialWorkbench";
import EHRConnect from "@/pages/EHRConnect";
import SmartCallback from "@/pages/SmartCallback";
import SandboxBanner from "@/components/SandboxBanner";
import ProviderPerformance from "@/pages/ProviderPerformance";
import ProviderRespond from "@/pages/ProviderRespond";
import PriorAuthInsights from "@/pages/PriorAuthInsights";
import ProviderCompare from "@/pages/ProviderCompare";
import CoverageIntelligence from "@/pages/CoverageIntelligence";


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") ? "http://localhost:8001" : "");
export const API = `${BACKEND_URL}/api`;

// Organization Types
export const ORG_TYPES = {
  provider: { label: "Healthcare Provider", description: "Clinics, hospitals, physician practices" },
  tpa: { label: "Third Party Administrator", description: "TPA managing PA reviews for multiple providers" },
  payer: { label: "Insurance Payer", description: "Insurance company or health plan" }
};

// User Roles
export const USER_ROLES = {
  admin: { label: "Admin", description: "Full organization management access" },
  staff: { label: "Staff", description: "Create and manage PA requests" },
  reviewer: { label: "Reviewer", description: "Review and process PA requests" },
  decision_maker: { label: "Decision Maker", description: "Final approval authority" }
};

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
      setOrganization(response.data.organization || null);
    } catch (error) {
      setUser(null);
      setOrganization(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    const redirectUrl = window.location.origin + '/pa-requests';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const loginWithPassword = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, 
        { email, password },
        { withCredentials: true }
      );
      setUser(response.data);
      setOrganization(response.data.organization || null);
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || "Login failed";
      return { success: false, error: message };
    }
  };

  const register = async (email, password, name, orgType, orgName) => {
    try {
      const response = await axios.post(`${API}/auth/register`, 
        { email, password, name, org_type: orgType, org_name: orgName },
        { withCredentials: true }
      );
      setUser(response.data);
      setOrganization(response.data.organization || null);
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || "Registration failed";
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
    setOrganization(null);
    window.location.href = '/';
  };

  const isAdmin = user?.role === 'admin';
  const isProvider = organization?.org_type === 'provider';
  const isTPA = organization?.org_type === 'tpa';
  const isPayer = organization?.org_type === 'payer';
  
  const permissions = user?.permissions || {
    can_create: false,
    can_edit: false,
    can_review: false,
    can_submit: false,
    can_manage_users: false,
    can_view_all: false
  };

  return (
    <AuthContext.Provider value={{ 
      user, setUser, 
      organization, setOrganization,
      loading, login, loginWithPassword, register, logout, checkAuth,
      isAdmin, isProvider, isTPA, isPayer, permissions
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading, isAdmin } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0f1a]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/pa-requests" replace />;
  }

  return children;
};

// App Router
function AppRouter() {
  const location = useLocation();

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/org-setup" element={<ProtectedRoute><OrgSetup /></ProtectedRoute>} />
      <Route path="/dashboard" element={<Navigate to="/pa-requests" replace />} />
      <Route path="/patients" element={<ProtectedRoute><Patients /></ProtectedRoute>} />
      <Route path="/coverage-intelligence" element={<ProtectedRoute><CoverageIntelligence /></ProtectedRoute>} />

      <Route path="/pa-requests" element={<ProtectedRoute><PARequests /></ProtectedRoute>} />
      <Route path="/pa-requests/new" element={<ProtectedRoute><NewPARequest /></ProtectedRoute>} />
      <Route path="/pa-requests/:paId/workflow" element={<ProtectedRoute><NewPARequest /></ProtectedRoute>} />
      <Route path="/form-library" element={<ProtectedRoute><FormLibrary /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
      <Route path="/insights" element={<ProtectedRoute><PriorAuthInsights /></ProtectedRoute>} />
      <Route path="/provider-performance" element={<ProtectedRoute><ProviderPerformance /></ProtectedRoute>} />
      <Route path="/provider-compare" element={<ProtectedRoute><ProviderCompare /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/user-management" element={<ProtectedRoute adminOnly><UserManagement /></ProtectedRoute>} />
      <Route path="/review-queue" element={<ProtectedRoute><ReviewQueue /></ProtectedRoute>} />
      <Route path="/appeals" element={<ProtectedRoute><Appeals /></ProtectedRoute>} />
      <Route path="/denied-claims" element={<ProtectedRoute><DenialWorkbench /></ProtectedRoute>} />
      <Route path="/ehr-connect" element={<ProtectedRoute><EHRConnect /></ProtectedRoute>} />
      <Route path="/smart-callback" element={<SmartCallback />} />
      <Route path="/provider-respond/:token" element={<ProviderRespond />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SandboxBanner />
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
